import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { disableScreenShare } from '../../App'
import { useClickOutside } from '../../hooks/useClickOutside'
import { useFileUpload } from '../../hooks/useFileUpload'
import { FileInput } from '../FileInput/FileInput'
import {
  CloseIcon,
  MuteIcon,
  ShareScreenIcon,
  StopIcon,
  UnmuteIcon,
} from '../Icons'

interface PiPControlsProps {
  // Audio controls
  volume: number
  isMuted: boolean
  onVolumeChange: (volume: number) => void
  onMuteToggle: () => void

  // Microphone controls
  isMicMuted?: boolean
  onMicMute: (muted: boolean) => void

  // Session controls
  isAuthenticated: boolean
  isScreenSharing: boolean
  onScreenShareClick: () => void
  onEndSession: () => void
  // Avatar talking state (passed from parent)
  isAvatarTalking?: boolean
  isFileUploading: boolean

  // Stop talking controls
  onStopTalking?: () => void
  dataChannel?: RTCDataChannel | null
  sessionId?: string

  // Upload controls
  avatarId: string
  organizationId?: string
  onTrackUploadEvent?: (event: string) => void
  showScreenShareButton: boolean
  isWatchSession: boolean
  socketId: string
}

export const PiPControls: React.FC<PiPControlsProps> = ({
  volume,
  isMuted,
  onVolumeChange,
  onMuteToggle,
  isMicMuted: initialMicMuted,
  onMicMute,
  isAuthenticated,
  isScreenSharing,
  onScreenShareClick,
  onEndSession,
  isAvatarTalking = false,
  onStopTalking,
  dataChannel,
  sessionId,
  avatarId,
  organizationId,
  onTrackUploadEvent,
  isFileUploading = false,
  showScreenShareButton,
  isWatchSession,
  socketId,
}) => {
  const [isMicMuted, setIsMicMuted] = React.useState(initialMicMuted)

  const [showDropdown, setShowDropdown] = useState(false)
  const [visibleButtons, setVisibleButtons] = useState<string[]>([])
  const [hiddenButtons, setHiddenButtons] = useState<string[]>([])
  const [showStopButton, setShowStopButton] = useState(isAvatarTalking)

  const containerRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)

  const { fileInputRef, handleFileChange } = useFileUpload({
    avatarId,
    organizationId: organizationId || '',
    onUploadStart: () => onTrackUploadEvent?.('upload_started'),
    onUploadSuccess: () => onTrackUploadEvent?.('upload_completed'),
    showToasts: false,
    dataChannel,
    socketId: socketId || '',
  })

  // Define button order (left to right, hide from right to left)
  const buttonOrder = useMemo(
    () => [
      ...(!isWatchSession ? ['micMute'] : []),
      'volume',
      ...(isAuthenticated && !isWatchSession ? ['upload'] : []),
      ...(!disableScreenShare ? ['screenShare'] : []),
      ...(showStopButton && !isWatchSession ? ['stopTalking'] : []),
      'endSession',
    ],
    [isAuthenticated, showStopButton],
  )

  const handleMicMute = () => {
    onMicMute(!isMicMuted)
    setIsMicMuted(!isMicMuted)
  }

  // Sync stop button visibility with avatar talking state (like Controls.tsx)
  useEffect(() => {
    if (isAvatarTalking) {
      setShowStopButton(true)
      return
    }
    // Hide button with delay like main page
    const timeout = setTimeout(() => {
      setShowStopButton(false)
    }, 300)
    return () => clearTimeout(timeout)
  }, [isAvatarTalking])

  // Calculate which buttons should be visible based on PiP window width
  const calculateVisibleButtons = useCallback(() => {
    // Get PiP window width instead of container width
    const pipWindow = containerRef.current?.ownerDocument?.defaultView
    const windowWidth = pipWindow?.innerWidth || 400

    // Calculate based on window width, not container width
    let maxVisibleButtons = buttonOrder.length

    // Simple breakpoints based on PiP window width
    if (windowWidth < 300) {
      maxVisibleButtons = 3
    } else if (windowWidth < 350) {
      maxVisibleButtons = 4
    } else if (windowWidth < 400) {
      maxVisibleButtons = 5
    }
    // else show all buttons

    if (maxVisibleButtons >= buttonOrder.length) {
      // All buttons fit
      setVisibleButtons(buttonOrder)
      setHiddenButtons([])
    } else {
      // Some buttons need to be hidden
      const visible = buttonOrder.slice(0, maxVisibleButtons - 1) // Reserve space for more button
      const hidden = buttonOrder.slice(maxVisibleButtons - 1)

      setVisibleButtons(visible)
      setHiddenButtons(hidden)
    }
  }, [buttonOrder])

  const handleStopTalking = () => {
    if (dataChannel && dataChannel.readyState === 'open') {
      dataChannel.send(
        JSON.stringify({
          type: 'cancel',
          data: { sessionId },
        }),
      )
    }

    onStopTalking?.()
  }

  // Set up ResizeObserver to watch PiP window size changes
  useEffect(() => {
    if (!containerRef.current) return

    const pipWindow = containerRef.current.ownerDocument?.defaultView
    if (!pipWindow) return

    const handleResize = () => {
      setShowDropdown(false) // Close dropdown on resize
      setTimeout(calculateVisibleButtons, 100)
    }

    pipWindow.addEventListener('resize', handleResize)

    return () => {
      pipWindow.removeEventListener('resize', handleResize)
    }
  }, [calculateVisibleButtons])

  // Initialize visible buttons on mount and when authentication changes
  useEffect(() => {
    setVisibleButtons(buttonOrder)
    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(calculateVisibleButtons, 100)
    return () => clearTimeout(timer)
  }, [buttonOrder, calculateVisibleButtons])

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => {
    setShowDropdown(false)
  })

  // Handle file upload click
  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  // Render individual button components
  const renderButton = (buttonId: string) => {
    switch (buttonId) {
      case 'micMute':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon w-[40px] align-middle"
            onClick={handleMicMute}
            title={isMicMuted ? 'Unmute Microphone' : 'Mute Microphone'}
          >
            {isMicMuted ? <MuteIcon /> : <UnmuteIcon />}
          </button>
        )

      case 'volume':
        return (
          <div key={buttonId} className="relative tc-mv-volume-wrapper">
            <div className="absolute flex justify-center bottom-full left-0 right-0 rounded-full pb-2">
              <div className="w-8 flex items-center justify-center rounded-full tc-mv-volume p-2">
                <input
                  type="range"
                  className="w-2 rounded-md h-full appearance-none bg-transparent cursor-pointer tc-mv-volume-slider"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={e => onVolumeChange(parseFloat(e.target.value))}
                  style={{
                    background: `linear-gradient(to top, white 0%, white ${(isMuted ? 0 : volume) * 100}%, rgba(0,0,0,0.4) ${(isMuted ? 0 : volume) * 100}%, rgba(0,0,0,0.4) 100%)`,
                  }}
                />
              </div>
            </div>
            <button
              className="tc-waveform-icon w-[40px] align-middle text-white"
              onClick={onMuteToggle}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <i className="fa-solid fa-volume-xmark" />
              ) : (
                <i className="fa-solid fa-volume-high" />
              )}
            </button>
          </div>
        )

      case 'upload':
        return isAuthenticated ? (
          <div key={buttonId}>
            <FileInput
              isFileUploading={isFileUploading}
              handleFileChange={handleFileChange}
              handleUploadClick={handleUploadClick}
              ref={fileInputRef}
            />
          </div>
        ) : null

      case 'screenShare':
        return showScreenShareButton ? (
          <button
            key={buttonId}
            className={`tc-waveform-icon w-[40px] align-middle ${
              isScreenSharing ? 'text-black !bg-white' : 'text-white'
            }`}
            onClick={onScreenShareClick}
            title={isScreenSharing ? 'Stop Screen Share' : 'Share Screen'}
          >
            <ShareScreenIcon />
          </button>
        ) : null

      case 'stopTalking':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-stop flex-shrink-0 talking"
            onClick={handleStopTalking}
            title="Stop Talking"
          >
            <StopIcon />
          </button>
        )

      case 'endSession':
        return (
          <button
            key={buttonId}
            className="tc-waveform-icon align-middle tc-dark-grey-bg tc-waveform-call-end flex-shrink-0 text-white w-[40px]"
            onClick={onEndSession}
            title="End Session"
          >
            <CloseIcon />
          </button>
        )

      default:
        return null
    }
  }

  return (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div
        data-testid="waveform-container"
        className="tc-waveform-wrapper align-middle expanded"
      >
        <div
          ref={containerRef}
          className="tc-chat-question-bar-container align-between relative"
        >
          {/* Render visible buttons */}
          {visibleButtons.map(buttonId => renderButton(buttonId))}

          {/* More button with dropdown for hidden buttons */}
          {hiddenButtons.length > 0 && (
            <div className="relative">
              <button
                ref={moreButtonRef}
                className="tc-waveform-icon w-[40px] align-middle text-white"
                onClick={() => setShowDropdown(!showDropdown)}
                title="More options"
              >
                <i className="fa-solid fa-ellipsis" />
              </button>

              {/* Dropdown menu (opens upward) */}
              {showDropdown && (
                <div className="absolute tc-mv-blur !backdrop-blur-none bottom-full !bg-danger left-1/2 transform -translate-x-1/2 mb-2 rounded-lg  z-50">
                  <div className="p-2 flex flex-col gap-2">
                    {hiddenButtons.map(buttonId => renderButton(buttonId))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
